import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useProjectData } from '../useProjectData';

interface UseDeviceSocketProps {
  driverUid: string;
}

export const useCommandSocket = ({ driverUid }: UseDeviceSocketProps) => {
  const socketRef = useRef<Socket | null>(null);
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [eldResponses, setEldResponses] = useState<any[]>([]);
  const [cameraResponses, setCameraResponses] = useState<any[]>([]);
  const [infos, setInfos] = useState<any[]>([]);
  const [errors, setErrors] = useState<any[]>([]);
  const [driverStatus, setDriverStatus] = useState<'on' | 'off' | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const { projectData } = useProjectData();

  useEffect(() => {
    const token = localStorage.getItem('token');

    const socket = io(projectData.socketUrl, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { which: 'admin', token },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      socket.emit('join-to-device-status', { driverUid });
      socket.emit('driver-status', { driverUid });
    });

    socket.on('disconnect', () => {
      console.warn('Socket disconnected');
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connect_error:', err.message);
    });

    socket.on('device-response', (data) => {
      if (!data || !data.command) return;

      if (data.command === 'eld_response') {
        setEldResponses((prev) => [data, ...prev]);
      } else if (data.command === 'app_command_response') {
        setEldResponses((prev) => [data, ...prev]);
      } else if (data.command === 'camera_response') {
        setCameraResponses((prev) => [data, ...prev]);
      }
    });

    socket.on('info', (data) => {
      setInfos((prev) => [data, ...prev]);
    });

    socket.on('error', (data) => {
      setErrors((prev) => [data, ...prev]);
    });

    socket.on('driver-status', (data) => {
      if (!data) return;
      setDriverStatus(data.status);
    });

    return () => {
      socket.emit('leave-from-driver', { driverUid });
      socket.disconnect();
    };
  }, [driverUid]);

  // ================= DRIVER STATUS HEARTBEAT =================
  useEffect(() => {
    if (!isConnected || !socketRef.current) return;

    statusIntervalRef.current = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('driver-status', { driverUid });
      }
    }, 10000);

    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
    };
  }, [isConnected, driverUid]);

  const sendCommand = useCallback(
    (command: string, text?: string) => {
      if (!socketRef.current || !isConnected) return;
      const payload = { driverUid, command: { command, text } };
      socketRef.current.emit('device-request', payload);
    },
    [driverUid, isConnected],
  );

  return {
    eldResponses,
    cameraResponses,
    infos,
    errors,
    driverStatus,
    sendCommand,
    isConnected,
  };
};
